import { useState, useEffect, useCallback } from 'react';
import { onboardingManager, OnboardingProgress } from '../utils/onboarding';

export const useOnboarding = () => {
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProgress = useCallback(() => {
    try {
      const currentProgress = onboardingManager.getProgress();
      setProgress(currentProgress);
    } catch (error) {
      console.error('Failed to load onboarding progress:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const completeStep = useCallback((stepId: string) => {
    onboardingManager.completeStep(stepId);
    loadProgress();
  }, [loadProgress]);

  const resetStep = useCallback((stepId: string) => {
    onboardingManager.resetStep(stepId);
    loadProgress();
  }, [loadProgress]);

  const dismissOnboarding = useCallback(() => {
    onboardingManager.dismissOnboarding();
    loadProgress();
  }, [loadProgress]);

  const showOnboarding = useCallback(() => {
    onboardingManager.showOnboarding();
    loadProgress();
  }, [loadProgress]);

  const resetOnboarding = useCallback(() => {
    onboardingManager.resetOnboarding();
    loadProgress();
  }, [loadProgress]);

  // Auto-completion methods
  const checkEventCreated = useCallback(() => {
    onboardingManager.checkEventCreated();
    loadProgress();
  }, [loadProgress]);

  const checkInventoryAdded = useCallback(() => {
    onboardingManager.checkInventoryAdded();
    loadProgress();
  }, [loadProgress]);

  const checkSaleRecorded = useCallback(() => {
    onboardingManager.checkSaleRecorded();
    loadProgress();
  }, [loadProgress]);

  const checkSupplierAdded = useCallback(() => {
    onboardingManager.checkSupplierAdded();
    loadProgress();
  }, [loadProgress]);

  const checkDataImported = useCallback(() => {
    onboardingManager.checkDataImported();
    loadProgress();
  }, [loadProgress]);

  const checkReportsViewed = useCallback(() => {
    onboardingManager.checkReportsViewed();
    loadProgress();
  }, [loadProgress]);

  const checkDataExported = useCallback(() => {
    onboardingManager.checkDataExported();
    loadProgress();
  }, [loadProgress]);

  return {
    progress,
    loading,
    completeStep,
    resetStep,
    dismissOnboarding,
    showOnboarding,
    resetOnboarding,
    // Auto-completion methods
    checkEventCreated,
    checkInventoryAdded,
    checkSaleRecorded,
    checkSupplierAdded,
    checkDataImported,
    checkReportsViewed,
    checkDataExported,
    // Utility methods
    shouldShowOnboarding: () => onboardingManager.shouldShowOnboarding(),
    getNextStep: () => onboardingManager.getNextStep(),
    getCompletionPercentage: () => onboardingManager.getCompletionPercentage(),
  };
};